import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../data'
import { IntakeLoop } from './IntakeLoop'
import { Thumb } from './Photos'

/**
 * Intake (§9.3), as a page: pick what is in the box, then scan.
 *
 * This is where a **blank label** lands — the API redirects a reserved serial
 * here, and at that point nobody has said what the thing in your hand is. So
 * the picker is the front door of this route and the loop follows it.
 *
 * Receiving something already on the shelf does not come through here at all:
 * the Stock tab runs the same `IntakeLoop` inside its Receive dialog, because
 * tapping a part whose photograph is on screen has already answered the
 * question this page has to ask.
 */
export function IntakeView({ initialSerial }: { initialSerial: string | null }): JSX.Element {
  const [typeId, setTypeId] = useState('')

  const types = useQuery({ queryKey: ['component-types'], queryFn: () => repository.componentTypes() })

  if (!typeId) {
    return (
      <div className="stack">
        <h1 className="page-title">Intake</h1>
        <p className="muted">What is in the box? Everything scanned next becomes this type.</p>
        {types.isPending ? <p className="muted">Loading types…</p> : null}
        {types.isError ? <p className="badge critical">Could not load component types.</p> : null}
        <div className="card-grid">
          {(types.data ?? []).map((t) => (
            <button key={t.id} className="card type-card" onClick={() => setTypeId(t.id)}>
              <Thumb imageId={t.imageId} label={t.name} size={72} />
              <span className="type-card__body">
                <strong className="type-card__name">{t.name}</strong>
                <span className="muted type-card__meta">
                  {t.category || 'no category'}
                  {t.supplier ? ` · ${t.supplier}` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
        {initialSerial ? (
          <p className="muted">
            <span className="serial">{initialSerial}</span> is a blank label. Pick its type and it
            will be the first one in.
          </p>
        ) : null}
      </div>
    )
  }

  const typeName = types.data?.find((t) => t.id === typeId)?.name ?? typeId

  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          {typeName}
        </h1>
        {/* Unmounting the loop is what releases the camera — `useScanner` stops
            its own tracks on cleanup, so there is nothing to remember here. */}
        <button onClick={() => setTypeId('')}>Change type</button>
      </div>
      <IntakeLoop typeId={typeId} />
    </div>
  )
}
